import isEmpty from 'lodash/isEmpty'
import React, { MouseEvent, useCallback, useEffect, useState } from 'react'
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  ColumnSizer,
  MultiGrid,
} from 'react-virtualized'
import { GridCellProps } from 'react-virtualized/dist/es/Grid'
import { css } from 'styled-components'

import { IconChevronDown, IconChevronUp } from '@avantstay/backoffice-vectors'

import { mainColors } from '../../styles/colors'
import * as S from './AutoSizeGrid.styles'
import { AutoSizeGridProps, CellClassNameOptions, RowWithSubRows } from './AutoSizeGrid.types'
import { getColumnValue, getDynamicColumnWidth } from './AutoSizeGrid.utils'
import { EmptyResults } from './components/EmptyResults'
import { HeaderRowCell } from './components/HeaderRowCell'

const RENDER_BORDER_BOTTOM_OFFSET = 6
/**
 * @deprecated import DataGrid from "@avantstay/backoffice-core" instead
 */
export function AutoSizeGrid<RowDataType>({
  columns,
  data,
  renderRowBelowHeader,
  maxWidth,
  minWidth = 100,
  fixedColumnCount = 1,
  resizable = true,
  onRowClick,
  styleByRow = () => css``,
  heightByRow,
  isLoadingContent,
  emptyDataComponent,
  customRowHeight,
  disabledHeaderCellFullWidth = false,
  hasStickyDataRowBelowHeader = false,
  customActiveRowHeight,
  ...multiGridProps
}: AutoSizeGridProps<RowDataType>): JSX.Element {
  const [resizedColumnsWidths, setResizedColumnsWidths] = useState<Record<number, number>>({})
  const [expandedRows, setExpandedRows] = useState<number[]>([])
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | undefined>()
  const [activeRowIndex, setActiveRowIndex] = useState<number>()
  const [shouldRenderBottomBorder, setShouldRenderBottomBorder] = useState<boolean>(false)
  const [scrollBarWidth, setScrollBarWidth] = useState<number>(0)

  const emptyData = [{} as RowWithSubRows<RowDataType>]

  const parsedData = () => {
    if (isLoadingContent) {
      return emptyData
    }

    if (data && data?.length > 0) {
      return data
    }

    return emptyData
  }

  const currentData = parsedData()

  // actions on first render
  useEffect(() => {
    // collapse all rows on data change
    setExpandedRows([])
    // clean any hovered row
    setHoveredRowIndex(undefined)
    // reset the active row index
    setActiveRowIndex(undefined)
  }, [data])

  const expandRow = useCallback((rowId?: number) => {
    if (rowId !== undefined) {
      setExpandedRows(currentExpandedRows => [...currentExpandedRows, rowId])
    }
  }, [])

  const collapseRow = useCallback((rowId?: number) => {
    if (rowId !== undefined) {
      setExpandedRows(currentExpandedRows => currentExpandedRows.filter(id => id !== rowId))
    }
  }, [])

  const toggleRow = useCallback((rowId?: number) => {
    if (rowId !== undefined) {
      setExpandedRows(currentExpandedRows => {
        if (currentExpandedRows.includes(rowId)) {
          return currentExpandedRows.filter(id => id !== rowId)
        }
        return [...currentExpandedRows, rowId]
      })
    }
  }, [])

  /**
   * This is used to ensure that there are always columns present
   * These is a known issue that using the back button on browsers in certain cases,
   * would case columns to be passed as undefined.
   * This prevents an issue being thrown, that caused users to manually refresh their views
   */
  if (columns == null) {
    return <></>
  }

  // calls action for each row
  const onRowClickAction = (
    rowIndex?: number,
    rowData?: RowDataType,
    e?: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
  ) => {
    if (activeRowIndex === rowIndex) {
      setActiveRowIndex(undefined)
    } else {
      setActiveRowIndex(rowIndex)
    }

    if (rowData !== undefined && onRowClick) {
      onRowClick(rowData, e)
    }
  }

  const storeColumnWidthDelta = (
    columnIndex: number,
    widthDelta: number,
    columnWidth?: number | string,
  ) => {
    setResizedColumnsWidths(widths => {
      const columnMinWidth = columns[columnIndex]?.minWidth ?? minWidth
      const delta = widthDelta + (widths[columnIndex] || 0)
      const nextWidth = (Number(columnWidth) || 0) + delta
      // Don't update if next width is less than minimum width
      const shouldUpdate = nextWidth >= columnMinWidth
      return {
        ...widths,
        [columnIndex]: shouldUpdate ? delta : widths[columnIndex],
      }
    })
  }
  // In case table needs a sticky row at the top between header and table body we need to add component using renderRowBelowHeader prop
  const hasRowBelowHeader = Boolean(renderRowBelowHeader)

  type DataTypeWithSubRow = RowWithSubRows<RowDataType> & {
    dataRowId?: number
    isSubRow?: boolean
  }

  const backgroundByRowIndex: string[] = [mainColors.white]
  if (renderRowBelowHeader || hasStickyDataRowBelowHeader) {
    backgroundByRowIndex.push(mainColors.neutralMedium)
  }
  let even = true
  const dataWithHeader = currentData.reduce(
    (rows, currentRow, rowIndex) => {
      const dataRowId = rowIndex
      rows.push({ ...currentRow, isSubRow: false, dataRowId })
      if (currentRow.subRows?.length && expandedRows.includes(dataRowId)) {
        rows.push(...currentRow.subRows.map(subRow => ({ ...subRow, isSubRow: true })))
        backgroundByRowIndex.push(
          mainColors.neutralMedium,
          ...currentRow.subRows.map(() => mainColors.white),
        )
        even = true
      } else {
        backgroundByRowIndex.push(even ? mainColors.neutralExtraLight : mainColors.white)
        even = !even
      }
      return rows
    },
    // we add new row for header and another one for below header
    (hasRowBelowHeader
      ? [currentData[0], currentData[0]]
      : [currentData[0]]) as DataTypeWithSubRow[],
  )

  const columnCount = columns.length || 0

  const ROW_HEIGHT = 60
  const BELOW_HEADER_ROW_HEIGHT = 30

  const cache = new CellMeasurerCache({
    defaultWidth: 120,
    minWidth,
    fixedHeight: true,
  })

  const getGridHeight = (heightValue: number) => {
    if (isLoadingContent || isEmpty(data)) {
      return ROW_HEIGHT
    }

    return heightValue
  }

  const cellRenderer = (gridCellProps: GridCellProps) => {
    const { key, columnIndex, parent, style, rowIndex } = gridCellProps
    const currentRowData = dataWithHeader && dataWithHeader[rowIndex]
    const currentColumn = columns[columnIndex]
    const currentDataKey = currentColumn.key
    const hasSubRows = currentRowData.subRows?.length
    const isLastRow = rowIndex === data?.length
    const hasSingleRow = data?.length === 1
    const isFirstColumn = columnIndex === 0
    const fixedColumn = columnIndex === fixedColumnCount - 1
    const isExpanded =
      hasSubRows &&
      currentRowData.dataRowId !== undefined &&
      expandedRows.includes(currentRowData.dataRowId)

    const currentColumnValue = getColumnValue({
      columnKey: currentDataKey,
      rowData: currentRowData,
    })

    const cellContent = () => {
      if (currentColumn.column) {
        return currentColumn.column?.({
          isLastRow,
          hasSingleRow,
          rowData: currentRowData,
          columnKey: currentDataKey,
          value: currentColumnValue,
          onToggleSubRows: () =>
            currentRowData.subRows?.length &&
            currentRowData.dataRowId !== undefined &&
            toggleRow(currentRowData.dataRowId),
          activeRow: rowIndex === activeRowIndex,
        })
      }

      return currentColumnValue
    }

    // Header row
    if (rowIndex === 0) {
      return (
        <HeaderRowCell
          disabledHeaderCellFullWidth={disabledHeaderCellFullWidth}
          key={key}
          gridCellProps={gridCellProps}
          isFixed={fixedColumn}
          columns={columns}
          cache={cache}
          onColumnResize={storeColumnWidthDelta}
          resizable={resizable}
          hasRowBelowHeader={hasRowBelowHeader || hasStickyDataRowBelowHeader}
        />
      )
    }

    // Below header row
    if (rowIndex === 1 && hasRowBelowHeader) {
      return columnIndex === 0 ? (
        <S.BelowHeaderContainer key={key}>{renderRowBelowHeader}</S.BelowHeaderContainer>
      ) : (
        <S.BelowHeaderContainer key={key} />
      )
    }

    if (isEmpty(data) || isLoadingContent) {
      return null
    }

    const getRowBackground = () => {
      return backgroundByRowIndex[rowIndex]
    }

    // handles if hover row or not
    const getHoverRowClassName = (hoverRowIndex?: number) => {
      setHoveredRowIndex(hoverRowIndex)
    }

    const getRowChevron = () => (
      <S.Chevron>
        {isExpanded ? (
          <IconChevronUp
            onClick={() => {
              collapseRow(currentRowData.dataRowId)
            }}
          />
        ) : (
          <IconChevronDown
            onClick={() => {
              if (currentRowData.dataRowId !== undefined) {
                expandRow(currentRowData.dataRowId)
              }
            }}
          />
        )}
      </S.Chevron>
    )

    const cellClassName =
      rowIndex === hoveredRowIndex || rowIndex === activeRowIndex
        ? CellClassNameOptions.hover
        : CellClassNameOptions.default

    const updatedCellClassName =
      isLastRow && shouldRenderBottomBorder ? `${cellClassName} renderBottomBorder` : cellClassName

    // Regular rows
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={columnIndex}
        key={key}
        parent={parent}
        rowIndex={rowIndex}
      >
        {() => (
          <S.GridCell
            key={key}
            isFirstColumn={isFirstColumn}
            fixedColumn={fixedColumn}
            isLastColumn={columnIndex === columnCount}
            style={style}
            borderStyle={currentColumn.borderStyle}
            background={getRowBackground()}
            withSeparator={currentRowData.isSubRow}
            hasRowBelowHeader={hasRowBelowHeader || hasStickyDataRowBelowHeader}
            className={updatedCellClassName}
            onClick={e => onRowClickAction(rowIndex, currentRowData, e)}
            onMouseOver={() => getHoverRowClassName(rowIndex)}
            onMouseLeave={() => getHoverRowClassName(undefined)}
            styleByRow={styleByRow(currentRowData)}
          >
            {cellContent()}
            {hasSubRows && isFirstColumn ? getRowChevron() : null}
          </S.GridCell>
        )}
      </CellMeasurer>
    )
  }

  const getRowHeight = (index: number) => {
    if (index === 0) return ROW_HEIGHT
    if ((index === 1 && hasRowBelowHeader) || (index === 1 && hasStickyDataRowBelowHeader))
      return BELOW_HEADER_ROW_HEIGHT

    if (heightByRow) {
      const currentRowData = dataWithHeader && dataWithHeader[index]
      const rowHeight = heightByRow(currentRowData)
      return rowHeight
    }

    if (activeRowIndex === index) {
      return customActiveRowHeight || customRowHeight || ROW_HEIGHT
    }

    return customRowHeight || ROW_HEIGHT
  }

  const getShouldRenderBottomBorder = (gridHeight: number) => {
    const totalRowHeight =
      data?.reduce((accHeight, currentRow, index) => {
        const rowHeight = getRowHeight(index)
        return accHeight + rowHeight
      }, 0) || 0
    const hasBottomBorder = gridHeight > totalRowHeight + RENDER_BORDER_BOTTOM_OFFSET

    setShouldRenderBottomBorder(hasBottomBorder)
  }

  return (
    <AutoSizer>
      {({ width, height }) => (
        <S.GridContainer>
          <ColumnSizer
            columnMaxWidth={maxWidth}
            columnMinWidth={minWidth}
            columnCount={columns.length}
            width={width}
          >
            {({ adjustedWidth, columnWidth, registerChild }) => {
              const dynamicColumnWidth = getDynamicColumnWidth({
                columnCount,
                columns,
                fallbackWidth: columnWidth,
                resizedColumnsWidths,
                scrollBarWidth,
                totalRowWidth: adjustedWidth,
              })

              getShouldRenderBottomBorder(height)

              return (
                <MultiGrid
                  ref={registerChild}
                  classNameBottomLeftGrid="gridBottomLeft"
                  columnWidth={({ index }) =>
                    Math.max(
                      columns[index].minWidth || minWidth,
                      (columns[index].width || dynamicColumnWidth) +
                        (resizedColumnsWidths[index] || 0),
                    )
                  }
                  columnCount={columnCount}
                  height={getGridHeight(height)}
                  fixedColumnCount={fixedColumnCount}
                  fixedRowCount={hasRowBelowHeader || hasStickyDataRowBelowHeader ? 2 : 1}
                  rowHeight={({ index }) => getRowHeight(index)}
                  rowCount={dataWithHeader.length}
                  enableFixedColumnScroll
                  width={adjustedWidth}
                  cellRenderer={cellRenderer}
                  onScrollbarPresenceChange={({ vertical, size }) => {
                    if (vertical) {
                      setScrollBarWidth(size)
                    } else {
                      setScrollBarWidth(0)
                    }
                  }}
                  style={{
                    borderTop: `1px solid ${mainColors.neutralMedium}`,
                    backgroundColor: mainColors.white,
                  }}
                  {...multiGridProps}
                />
              )
            }}
          </ColumnSizer>
          {(isLoadingContent || isEmpty(data)) && (
            <EmptyResults
              height={height}
              width={width}
              emptyDataComponent={emptyDataComponent}
              isLoadingContent={isLoadingContent}
            />
          )}
        </S.GridContainer>
      )}
    </AutoSizer>
  )
}
